import React, { useState, useCallback, useMemo } from 'react';
import ResultsDisplay from '../components/ResultsDisplay';
import GaugeComponent from 'react-gauge-component';
import { 
  performSpeedTest, 
  formatSpeed,
} from '../utils/speedTest';
import { FileSize, TestResult, SpeedUnit } from '../types';
import { useTheme } from '../contexts/ThemeContext';

// Define the single file size used for testing
const TEST_FILE_SIZE: FileSize = '100MB';

// Helper to convert speed to a value for the gauge (always in Mbps)
const convertSpeedToGaugeValue = (speed: number | null, unit: SpeedUnit): number => {
  if (speed === null) return 0;
  switch (unit) {
    case 'Kbps': return speed / 1000;
    case 'Gbps': return speed * 1000;
    case 'Mbps': 
    default: return speed;
  }
};

// Determine max value for the gauge based on current speed (in Mbps)
const getGaugeMaxValue = (currentSpeedMbps: number): number => {
  if (currentSpeedMbps < 8) return 10;     // 0-10 Mbps scale
  if (currentSpeedMbps < 80) return 100;    // 0-100 Mbps scale
  return 1000;   // 0-1000 Mbps (1 Gbps) scale
};

// Function to format tick/value labels - more precise for bandwidth style
const formatTickValue = (value: number): string => {
  if (value === 0) return '0';
  // Convert Mbps value back to appropriate scale for display
  if (value >= 1000) { // Gbps
    const valGbps = value / 1000;
    return `${Math.round(valGbps)}G`;
  } else if (value >= 1 || value === 0) { // Mbps
     return `${Math.round(value)}M`;
  } else { // Kbps
     const valKbps = value * 1000;
     return `${Math.round(valKbps)}K`;
  }
};

// Generate ticks based on maxValue - More ticks for bandwidth look
// Let's try fewer, more spaced out ticks for a cleaner look
const generateGaugeTicks = (maxValue: number): Array<{ value: number }> => {
    const ticks: Array<{ value: number }> = [];
    let tickValues: number[] = [];

    if (maxValue === 10) {
        tickValues = [0, 2, 4, 6, 8, 10];
    } else if (maxValue === 100) {
        tickValues = [0, 20, 40, 60, 80, 100];
    } else { // 1000
        tickValues = [0, 200, 400, 600, 800, 1000];
    }
    tickValues.forEach(v => ticks.push({ value: v }));
    return ticks;
};

const SpeedTest: React.FC = () => {
  const { mode } = useTheme();
  const isLight = mode === 'light';
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [testProgress, setTestProgress] = useState<number>(0);
  const [realtimeSpeed, setRealtimeSpeed] = useState<number | null>(null);
  const [speedUnit, setSpeedUnit] = useState<SpeedUnit>('Mbps');
  const [testResult, setTestResult] = useState<TestResult>({
    speed: null,
    error: null,
    time: null,
    size: null,
    unit: 'Mbps',
    bytesReceived: 0,
    durationLimited: false
  });
  const [testFinished, setTestFinished] = useState<boolean>(false);
  const [abortController, setAbortController] = useState<AbortController | null>(null);

  // Function to reset the test state
  const resetTest = useCallback(() => {
    // Reset all states
    setTestResult({
      speed: null,
      error: null,
      time: null,
      size: null,
      unit: 'Mbps',
      bytesReceived: 0,
      durationLimited: false
    });
    setRealtimeSpeed(null);
    setSpeedUnit('Mbps');
    setTestProgress(0);
    setTestFinished(false);
    setIsLoading(false);
    setAbortController(null);
  }, []);

  // Function to cancel the test
  const cancelTest = useCallback(() => {
    if (abortController) {
      abortController.abort();
      resetTest();
    }
  }, [abortController, resetTest]);

  // Function to run the single test
  const runSingleTest = useCallback(async () => {
    setIsLoading(true);
    setTestFinished(false);
    setTestProgress(0);
    setRealtimeSpeed(null);
    
    // Create new AbortController
    const controller = new AbortController();
    setAbortController(controller);
    
    console.log('Starting the single speed test...');
    try {
      const result = await performSpeedTest(
        (progress) => {
          setTestProgress(progress);
        },
        (speedData) => {
          setRealtimeSpeed(speedData.value);
          setSpeedUnit(speedData.unit);
        },
        controller.signal
      );
      
      console.log('Test completed, result:', result);
      setTestResult(result);
      setTestFinished(true);
      // Ensure final gauge reflects the result
      setRealtimeSpeed(result.speed);
      setSpeedUnit(result.unit || 'Mbps');
      
    } catch (error) {
       console.error('Error during speed test execution (component catch):', error);
       // Only set error if not aborted
       if (error instanceof Error && error.name !== 'AbortError') {
         setTestResult({
            speed: null,
            error: error instanceof Error ? error.message : 'An unexpected error occurred during the test',
            time: null,
            size: TEST_FILE_SIZE,
            unit: 'Mbps',
            bytesReceived: 0,
            durationLimited: false
          });
         setTestFinished(true);
       }
    } finally {
      setIsLoading(false);
      setAbortController(null);
    }
  }, []); // No dependencies needed here

  // Start/Retry/Cancel Test Handler
  const handleTest = useCallback(() => {
    if (isLoading) {
      // If test is running, cancel it
      cancelTest();
    } else {
      // If no test is running, start a new one
      resetTest(); // Reset state completely
      runSingleTest();
    }
  }, [isLoading, resetTest, runSingleTest, cancelTest]);

  // Calculate gauge value (always in Mbps) and max value dynamically
  const gaugeValue = useMemo(() => {
    const speedToConvert = realtimeSpeed ?? (testFinished ? testResult.speed : null);
    const unitToConvert = speedUnit ?? (testFinished ? testResult.unit : 'Mbps');
    return convertSpeedToGaugeValue(speedToConvert, unitToConvert ?? 'Mbps');
  }, [realtimeSpeed, testFinished, testResult.speed, speedUnit, testResult.unit]);
  
  const gaugeMaxValue = useMemo(() => getGaugeMaxValue(gaugeValue), [gaugeValue]);

  // Generate ticks dynamically
  const gaugeTicks = useMemo(() => generateGaugeTicks(gaugeMaxValue), [gaugeMaxValue]);

  // Format value for the center label
  const formatGaugeTextValue = useCallback(() => {
    if (!isLoading && realtimeSpeed === null && testResult.speed === null && !testResult.error) {
      return '--';
    }
    // Display error if present
    if(testResult.error && !isLoading) return 'Error';
    
    const speed = realtimeSpeed ?? testResult.speed;
    const unit = speedUnit ?? testResult.unit ?? 'Mbps';
    
    if (speed === null) return '--';

    // Convert speed back to original units for display using formatSpeed
    const displayValue = formatSpeed(convertSpeedToGaugeValue(speed, unit));
    return `${Math.round(displayValue.value)} ${displayValue.unit}`; 
  }, [isLoading, realtimeSpeed, testResult.speed, speedUnit, testResult.unit, testResult.error]);
  

  return (
    <div className="min-h-full flex flex-col items-center">
      {/* Main card with theme-aware design */}
      <div className={`
        w-full max-w-2xl p-6 md:p-8 border flex flex-col h-[400px] md:h-[450px]
        ${isLight 
          ? 'bg-white border-gray-200 shadow-sm' 
          : 'bg-gray-900 border-green-500/30'
        }
      `}>
        <h2 className={`
          text-xl md:text-2xl font-mono text-center mb-6 md:mb-8
          ${isLight ? 'text-gray-900' : 'text-green-500'}
        `}>
          Speed Test
        </h2>
        
        {/* Fixed height container for gauge/results */}
        <div className="flex-1 flex items-center justify-center mb-6 md:mb-8">
          <div className="w-full max-w-md">
            {(!testResult.error && (!testResult.speed || isLoading)) ? (
              /* Gauge Component - Theme-aware Style */
              <div className="w-full aspect-[2/1] relative">
                <GaugeComponent
                  id="speed-gauge-bandwidth"
                  minValue={0}
                  value={gaugeValue}
                  maxValue={gaugeMaxValue}
                  type="semicircle"
                  arc={{
                    nbSubArcs: 150,
                    colorArray: isLight 
                      ? ['#f8fafc', '#e5e5e5', '#525252']  // gray-50 -> gray-200 -> gray-600
                      : ['#022c22', '#059669', '#34d399'],  // dark green -> emerald -> green
                    subArcs: [{}],
                    width: 0.15,
                    padding: 0.001,
                  }}
                  pointer={{
                    type: 'needle',
                    color: isLight ? '#525252' : '#059669',
                    baseColor: isLight ? '#525252' : '#059669',
                    animate: true,
                    animationDuration: 200,
                    length: 0.7,
                    width: 4
                  }}
                  labels={{
                    valueLabel: {
                      formatTextValue: formatGaugeTextValue,
                      style: {
                        fontSize: '32px',
                        fill: isLight ? '#404040' : '#34d399',  // gray-700 : green-400
                        fontFamily: 'monospace',
                        textShadow: isLight 
                          ? '0 0 8px rgba(82, 82, 82, 0.2)'  // gray-600
                          : '0 0 8px rgba(52, 211, 153, 0.3)'  // green-400
                      }
                    },
                    tickLabels: {
                      type: 'outer',
                      ticks: gaugeTicks,
                      hideMinMax: false,
                      defaultTickValueConfig: {
                        formatTextValue: formatTickValue,
                        style: { 
                          fontSize: 10, 
                          fill: isLight ? '#525252' : '#059669',  // gray-600
                          fontFamily: 'monospace'
                        }
                      },
                      defaultTickLineConfig: {
                        color: 'transparent',  // Hide tick lines
                        length: 0,  // Set length to 0
                        width: 0,  // Set width to 0
                        distanceFromArc: 12  // Increase distance from arc
                      }
                    }
                  }}
                />
                
                {/* Progress Bar below gauge */}
                {isLoading && (
                  <div className="absolute bottom-0 left-0 right-0 mx-auto" style={{ width: '60%', maxWidth: '240px' }}>
                    <div className={`
                      w-full h-1 border
                      ${isLight 
                        ? 'bg-gray-50 border-gray-200' 
                        : 'bg-gray-800 border-green-500/20'
                      }
                    `}>
                      <div 
                        className={`
                          h-full transition-all duration-150 ease-linear
                          ${isLight ? 'bg-gray-600' : 'bg-green-500'}
                        `}
                        style={{ width: `${testProgress}%` }}>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              /* Results Display */
              <ResultsDisplay result={testResult} />
            )}
          </div>
        </div>

        {/* Test Button - Theme-aware style */}
        <div className="flex justify-center mt-auto">
          <button
            onClick={handleTest}
            className={`
              w-56 py-3 font-mono border transition-colors duration-200
              focus:outline-none focus:ring-2 text-center
              ${isLight 
                ? isLoading
                  ? 'bg-white border-gray-600 text-gray-600 hover:bg-gray-50'
                  : 'bg-white border-gray-600 text-gray-600 hover:bg-gray-50 focus:ring-gray-200'
                : isLoading
                  ? 'bg-gray-900 border-green-500 text-green-500 hover:bg-green-500/10'
                  : 'bg-gray-900 border-green-500 text-green-500 hover:bg-green-500/10 focus:ring-green-500/50'
              }
            `}
          >
            {isLoading ? 'Cancel Test' : (testFinished || testResult.error) ? 'Retry Test' : 'Start Test'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SpeedTest; 